import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { KeywordChipInput } from "./KeywordChipInput";
import { ComposedQueryPreview } from "./ComposedQueryPreview";
import { JobChangeAnnouncement } from "./JobChangeAnnouncement";
import { composeBooleanQuery, parseBooleanQuery } from "./booleanQuery";
import {
	DEFAULT_JOB_CHANGE_LANGUAGE,
	JobChangeLanguage,
	LANGUAGE_PHRASES,
	findLanguageByPhrase,
} from "./jobChangeLanguages";
import { BooleanChips, BooleanOperator, Chip } from "./types";

interface TopicQueryBuilderProps {
	/** Existing stored query (for edit/duplicate), parsed once on mount. */
	initialQuery?: string | null;
	error?: boolean;
	/** Emits the composed Boolean query string whenever it changes. */
	onQueryChange: (query: string) => void;
	/** Called when the job-change toggle is switched on (parent auto-checks `posts`). */
	onJobChangeEnabled: () => void;
}

interface BuilderState {
	chips: BooleanChips;
	jobChangeOn: boolean;
	language: JobChangeLanguage;
	/** When set, a preserved free-form/legacy query emitted verbatim until the first edit. */
	customRaw: string | null;
}

/** Builds the initial state by parsing the stored query (runs once on mount). */
function hydrate(initialQuery: string | null | undefined): BuilderState {
	const parsed = parseBooleanQuery(initialQuery);

	if (!parsed.ok && parsed.raw) {
		return {
			chips: { and: [], or: [], not: [] },
			jobChangeOn: false,
			language: DEFAULT_JOB_CHANGE_LANGUAGE,
			customRaw: parsed.raw,
		};
	}

	// Detect a job-change phrase among the AND chips and lift it into the toggle.
	let jobChangeOn = false;
	let language = DEFAULT_JOB_CHANGE_LANGUAGE;
	const and: Chip[] = [];

	parsed.chips.and.forEach((chip) => {
		const matchedLanguage = findLanguageByPhrase(chip.value);

		if (matchedLanguage && !jobChangeOn) {
			jobChangeOn = true;
			language = matchedLanguage;

			return;
		}

		and.push(chip);
	});

	return {
		chips: { and, or: parsed.chips.or, not: parsed.chips.not },
		jobChangeOn,
		language,
		customRaw: null,
	};
}

/** Appends the derived locked job-change chip to the AND row when enabled. */
function getEffectiveChips(state: BuilderState): BooleanChips {
	if (!state.jobChangeOn) {
		return state.chips;
	}

	return {
		...state.chips,
		and: [...state.chips.and, { value: LANGUAGE_PHRASES[state.language], locked: true }],
	};
}

export const TopicQueryBuilder: FunctionComponent<TopicQueryBuilderProps> = ({
	initialQuery,
	error = false,
	onQueryChange,
	onJobChangeEnabled,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liPost.${key}`);

	const [state, setState] = useState<BuilderState>(() => hydrate(initialQuery));

	const composedQuery = useMemo(
		() =>
			state.customRaw !== null ? state.customRaw : composeBooleanQuery(getEffectiveChips(state)),
		[state]
	);

	// Emit the composed query without re-subscribing to a fresh callback each render.
	const onQueryChangeRef = useRef(onQueryChange);

	onQueryChangeRef.current = onQueryChange;

	const isInitialMountRef = useRef(true);

	useEffect(() => {
		// Skip the mount emit: the stored/initial query is already in the parent's
		// form state, so emitting here would trigger validation (and show the
		// "required" error) just from opening the page. Only emit on real edits.
		if (isInitialMountRef.current) {
			isInitialMountRef.current = false;

			return;
		}

		onQueryChangeRef.current(composedQuery);
	}, [composedQuery]);

	const addChip = (operator: BooleanOperator, value: string) => {
		const normalized = value.trim();

		if (!normalized) {
			return;
		}

		setState((prev) => {
			const existing = prev.chips[operator];
			const isDuplicate = existing.some(
				(chip) => chip.value.toLowerCase() === normalized.toLowerCase()
			);

			if (isDuplicate && prev.customRaw === null) {
				return prev;
			}

			return {
				...prev,
				customRaw: null,
				chips: isDuplicate
					? prev.chips
					: { ...prev.chips, [operator]: [...existing, { value: normalized }] },
			};
		});
	};

	const removeChip = (operator: BooleanOperator, index: number) => {
		setState((prev) => ({
			...prev,
			customRaw: null,
			chips: {
				...prev.chips,
				[operator]: prev.chips[operator].filter((_, i) => i !== index),
			},
		}));
	};

	const toggleJobChange = () => {
		const next = !state.jobChangeOn;

		setState((prev) => ({ ...prev, customRaw: null, jobChangeOn: next }));

		if (next) {
			onJobChangeEnabled();
		}
	};

	const changeLanguage = (language: JobChangeLanguage) => {
		setState((prev) => ({ ...prev, language }));
	};

	const lockedChip = state.jobChangeOn
		? { value: LANGUAGE_PHRASES[state.language], locked: true }
		: null;

	return (
		<div className="flex flex-col w-full mb-8">
			<label className="text-sm mb-1.5 font-medium text-gray-900">{ts("keywordBuilder")}</label>
			<p className="text-xs text-text-meta mb-4 leading-relaxed">{ts("keywordBuilderIntro")}</p>

			<div
				className={classNames(
					"flex flex-col gap-3 rounded-md p-1.5",
					error ? "ring-1 ring-status-error" : ""
				)}
			>
				<KeywordChipInput
					chips={state.chips.and}
					help={ts("andHelp")}
					label={ts("andLabel")}
					lockedChip={lockedChip}
					operator="and"
					placeholder={ts("keywordPlaceholderAnd")}
					onAdd={(value) => addChip("and", value)}
					onRemove={(index) => removeChip("and", index)}
				/>
				<KeywordChipInput
					chips={state.chips.or}
					help={ts("orHelp")}
					label={ts("orLabel")}
					operator="or"
					placeholder={ts("keywordPlaceholderOr")}
					onAdd={(value) => addChip("or", value)}
					onRemove={(index) => removeChip("or", index)}
				/>
				<KeywordChipInput
					chips={state.chips.not}
					help={ts("notHelp")}
					label={ts("notLabel")}
					operator="not"
					placeholder={ts("keywordPlaceholderNot")}
					onAdd={(value) => addChip("not", value)}
					onRemove={(index) => removeChip("not", index)}
				/>
			</div>

			<ComposedQueryPreview isCustom={state.customRaw !== null} query={composedQuery} />

			<JobChangeAnnouncement
				enabled={state.jobChangeOn}
				language={state.language}
				onLanguageChange={changeLanguage}
				onToggle={toggleJobChange}
			/>
		</div>
	);
};
